/**
 * Push subscription helper for PWA.
 * Call enablePushForCurrentUser() after login to register the browser
 * with the backend so the user receives push notifications even when
 * the app tab is closed (mobile + desktop with browser running).
 */
import api from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const STORAGE_KEY = 'push_subscribed_endpoint';

export async function enablePushForCurrentUser({ silent = true } = {}) {
  // Browser support
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    return { ok: false, reason: 'insecure-context' };
  }

  // Permission
  let perm = Notification.permission;
  if (perm === 'default') {
    if (silent) return { ok: false, reason: 'permission-default' };
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') return { ok: false, reason: `permission-${perm}` };

  // Get VAPID public key
  let vapidRes;
  try {
    vapidRes = await api.get('/push/vapid-public-key');
  } catch {
    return { ok: false, reason: 'vapid-fetch-failed' };
  }
  const vapidKey = vapidRes?.data?.public_key;
  if (!vapidKey) return { ok: false, reason: 'vapid-missing' };

  // Subscribe
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // If the existing subscription matches the same VAPID key, reuse it
    const existing = sub.options?.applicationServerKey;
    if (!existing) {
      try { await sub.unsubscribe(); } catch { /* noop */ }
      sub = null;
    }
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    } catch (e) {
      return { ok: false, reason: 'subscribe-failed', error: String(e) };
    }
  }

  // Send subscription to backend
  const payload = sub.toJSON();
  try {
    await api.post('/push/subscribe', {
      endpoint: payload.endpoint,
      keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
    });
    localStorage.setItem(STORAGE_KEY, payload.endpoint);
    return { ok: true };
  } catch {
    return { ok: false, reason: 'backend-save-failed' };
  }
}

export async function disablePushForCurrentUser() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      try { await sub.unsubscribe(); } catch { /* noop */ }
      try { await api.post('/push/unsubscribe', { endpoint }); } catch { /* noop */ }
    }
    localStorage.removeItem(STORAGE_KEY);
    return { ok: true };
  } catch {
    return { ok: false };
  }
}
